import React, { useEffect } from 'react';
import {
	GridColDef,
	gridFilterModelSelector,
	GridLogicOperator,
	GridSortModel,
	gridSortModelSelector,
	useGridApiRef,
} from '@mui/x-data-grid-pro';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import { ServerDataGrid } from '@warehouse/shared/ui';
import { objectBrowserTableColumns } from '../columns';
import { CustomColumnMenu } from './CustomColumnMenu';
import { CustomGridCell } from './custom-grid-cell/CustomGridCell';
import { NoObjectsFoundOverlay } from './NoObjectsFoundOverlay';
import { LicensorColumnName, TitleColumnName } from '../specific-column-names';
import { fromTitleSelector, useFromTitle } from '../domain/from-title.store';
import { objectBrowserGridConfig } from './object-browser.grid-config';

export function ObjectBrowserTable({ disableVirtualization }: TableProps) {
	const items = useObjectBrowserStore(objectBrowserStoreSelector.items);
	const isPending = useObjectBrowserStore(objectBrowserStoreSelector.isPending);
	const isInSearchMode = useObjectBrowserStore(objectBrowserStoreSelector.isInSearchMode);
	const selectedNxObjectUuid = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectUuid);
	const { selectNxObject } = useObjectBrowserStore(objectBrowserStoreSelector.actions);
	const status = useObjectBrowserStore(objectBrowserStoreSelector.status);
	const fromTitle = useFromTitle(fromTitleSelector.fromTitle);

	const apiRef = useGridApiRef();

	/**
	 * It runs code when entering or exiting search mode
	 */
	useEffect(() => {
		if (!apiRef) return;
		const currentSortModel = gridSortModelSelector(apiRef);

		if (isInSearchMode) {
			// Side effect when entering search mode
			if (status !== 'title-objects-tab-ready') {
				apiRef.current.setSortModel(replaceNameByKey(currentSortModel));
			}
			apiRef.current.setColumnVisibility('bucket', true);
		} else {
			// Side effect when exiting search mode
			apiRef.current.setSortModel(replaceKeyByName(currentSortModel));
			apiRef.current.setColumnVisibility('bucket', false);
		}
	}, [apiRef, isInSearchMode, status]);

	/**
	 * When loading the Object Browser Table by coming from the Titles Object tab, we add a filter to only show
	 * the objects that have the title assigned to them.
	 */
	useEffect(() => {
		if (!apiRef.current) return;

		if (fromTitle) {
			const currentFilterModel = gridFilterModelSelector(apiRef);

			// This prevents resetting the same filters when reloading the page if one of them is already set
			if (
				currentFilterModel.items.find(
					(item) => item.field === TitleColumnName && item.value === fromTitle.titleSearch,
				) ||
				currentFilterModel.items.find(
					(item) => item.field === LicensorColumnName && item.value === fromTitle.licensorLabel,
				)
			) {
				return;
			}

			const newFilterModel = {
				...currentFilterModel,
				items: [
					...(currentFilterModel.items || []),
					{
						field: TitleColumnName,
						operator: 'contains',
						value: fromTitle.titleSearch,
					},
					{
						field: LicensorColumnName,
						operator: 'contains',
						value: fromTitle.licensorLabel,
					},
				],
				logicOperator: GridLogicOperator.And,
			};
			apiRef.current.setFilterModel(newFilterModel);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiRef, fromTitle]);

	return (
		<ServerDataGrid
			$enableDrillableStyleOnFolders={!isInSearchMode}
			$variant="full"
			apiRef={apiRef}
			columns={objectBrowserTableColumns}
			rows={items ?? []}
			useStore={useObjectBrowserStore}
			selector={objectBrowserStoreSelector}
			config={objectBrowserGridConfig}
			getRowId={(row) => row.uuid}
			loading={isPending}
			slotProps={{
				// This prevents the itemType column from showing up in the columns visibility menu
				columnsManagement: {
					getTogglableColumns: (columns: GridColDef[]) =>
						columns.filter((column) => column.field !== 'itemType').map((column) => column.field),
				},
			}}
			onRowClick={({ row }) => selectNxObject(row)}
			rowSelection={false}
			getRowClassName={({ row }) =>
				`${row.itemType} ${selectedNxObjectUuid && row.uuid === selectedNxObjectUuid ? 'selected' : ''}`
			}
			slots={{
				columnMenu: CustomColumnMenu,
				cell: CustomGridCell,
				noRowsOverlay: NoObjectsFoundOverlay,
			}}
			disableVirtualization={disableVirtualization}
		/>
	);
}

export interface TableProps {
	/**
	 * @deprecated The method should only be used for component tests
	 */
	disableVirtualization?: boolean;
}

function replaceNameByKey(sortModel: GridSortModel) {
	return sortModel.map((sort) => {
		if (sort.field === 'name') {
			return { ...sort, field: 'key' };
		}
		return sort;
	});
}

function replaceKeyByName(sortModel: GridSortModel) {
	return sortModel.map((sort) => {
		if (sort.field === 'key') {
			return { ...sort, field: 'name' };
		}
		return sort;
	});
}
